import { useState, useEffect, useRef } from 'react';
import { InvitationStatusTicketEnum, Ticket } from '@pages/event/api/useFetchTicketSegment';
import { useUpdateTickets } from '@pages/event/api/useUpdateTickets';
import { useToast } from '@shared/hooks/useToast';

interface UseInvitationStatusProps {
  ticket: Ticket;
  eventDateEnd?: string;
  refetchSegments?: () => void;
  refetchCategories?: () => void;
  onConfirmStatusChange?: () => void;
}

export const useInvitationStatus = ({
  ticket,
  eventDateEnd,
  refetchSegments,
  refetchCategories,
  onConfirmStatusChange,
}: UseInvitationStatusProps) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [showConfirmationAlert, setShowConfirmationAlert] = useState(false);
  const [localStatus, setLocalStatus] = useState<InvitationStatusTicketEnum | null>(null);
  const dropdownRef = useRef<HTMLButtonElement>(null);
  const confirmationAlertRef = useRef<HTMLDivElement | null>(null);
  const { updateTickets, fetching: updateFetching } = useUpdateTickets();
  const { showToast } = useToast();

  const inviteStatus = localStatus ?? ticket.invitationStatus ?? null;

  const checkDatesNotPassed = () => {
    if (!eventDateEnd) return true;
    const eventEndDate = new Date(eventDateEnd);
    const now = new Date();
    return eventEndDate > now;
  };

  const handleConfirmStatusChange = async () => {
    try {
      const variables: { ticketIds: number[]; invitationStatus: string } = {
        ticketIds: [ticket.id],
        invitationStatus: InvitationStatusTicketEnum.ACCEPTED,
      };

      const result = await updateTickets(variables);

      if (result.error) {
        showToast('Ошибка при обновлении статуса приглашения', 'error');
        setLocalStatus(null);
        setShowConfirmationAlert(false);
        return;
      }

      setShowConfirmationAlert(false);
      refetchSegments?.();
      refetchCategories?.();
      onConfirmStatusChange?.();
    } catch (error) {
      console.error(error);
      showToast('Ошибка при обновлении статуса приглашения', 'error');
      setLocalStatus(null);
      setShowConfirmationAlert(false);
    }
  };

  const handleCancelStatusChange = () => {
    setShowConfirmationAlert(false);
    setLocalStatus(null);
    setIsDropdownOpen(false);
  };

  const handleDeclineStatusChange = async () => {
    setIsDropdownOpen(false);
    setShowConfirmationAlert(false);

    if (!checkDatesNotPassed()) {
      return;
    }

    setLocalStatus(InvitationStatusTicketEnum.DECLINED);

    try {
      const variables: { ticketIds: number[]; invitationStatus: string } = {
        ticketIds: [ticket.id],
        invitationStatus: InvitationStatusTicketEnum.DECLINED,
      };

      const result = await updateTickets(variables);

      if (result.error) {
        showToast('Ошибка при обновлении статуса приглашения', 'error');
        setLocalStatus(null);
        return;
      }

      refetchSegments?.();
      refetchCategories?.();
      onConfirmStatusChange?.();
    } catch (error) {
      console.error(error);
      showToast('Ошибка при обновлении статуса приглашения', 'error');
      setLocalStatus(null);
    }
  };

  const handleSelectAccepted = () => {
    setIsDropdownOpen(false);

    setLocalStatus(InvitationStatusTicketEnum.ACCEPTED);

    if (!checkDatesNotPassed()) {
      handleConfirmStatusChange();
      return;
    }

    setShowConfirmationAlert(true);
  };

  useEffect(() => {
    if (!showConfirmationAlert) return;

    const handleOutsideAlertClick = (event: MouseEvent) => {
      if (confirmationAlertRef.current && !confirmationAlertRef.current.contains(event.target as Node)) {
        handleCancelStatusChange();
      }
    };

    document.addEventListener('mousedown', handleOutsideAlertClick);

    return () => {
      document.removeEventListener('mousedown', handleOutsideAlertClick);
    };
  }, [showConfirmationAlert]);

  return {
    inviteStatus,
    isDropdownOpen,
    showConfirmationAlert,
    dropdownRef,
    confirmationAlertRef,
    updateFetching,
    setIsDropdownOpen,
    handleSelectAccepted,
    handleDeclineStatusChange,
    handleConfirmStatusChange,
    handleCancelStatusChange,
  };
};
